import type { AiChatLayoutItem, AiChatTimelineItem } from "@/lib/types/ai-chat";
import { replaceEqualDeep } from "@tanstack/react-query";

export interface ChatLayoutData {
  items: AiChatLayoutItem[];
  revision: number;
  complete: boolean;
}

export function layoutFromItem(item: AiChatTimelineItem): AiChatLayoutItem {
  return {
    id: item.id,
    sequence: item.sequence,
    endSequence:
      typeof item.payload.endSequence === "number"
        ? item.payload.endSequence
        : item.sequence,
    revision: item.revision,
    kind: item.kind,
    status: item.status,
    textCharacters: item.text?.length ?? 0,
    textLines: (item.text?.match(/\n/g) ?? []).length,
    activity: item.kind === "tool_activity" ? item.payload : {},
  };
}

export function mergeChatLayout(
  previous: ChatLayoutData | undefined,
  incoming: ChatLayoutData,
): ChatLayoutData {
  if (previous === incoming) return incoming;
  const entries = new Map(previous?.items.map((item) => [item.id, item]));
  for (const item of incoming.items) {
    const current = entries.get(item.id);
    if (
      !current ||
      (item.endSequence >= current.endSequence &&
        item.revision > current.revision) ||
      (item.revision === current.revision &&
        item.endSequence >= current.endSequence)
    )
      entries.set(item.id, {
        ...item,
        textCharacters: Math.max(
          item.textCharacters,
          current?.textCharacters ?? 0,
        ),
        textLines: Math.max(item.textLines, current?.textLines ?? 0),
      });
  }
  return replaceEqualDeep(previous, {
    items: [...entries.values()].sort((a, b) => a.sequence - b.sequence),
    revision: Math.max(previous?.revision ?? 0, incoming.revision),
    complete: incoming.complete || previous?.complete === true,
  });
}
